import { useMemo } from 'react';
// Utils
import cn from '../../../../utils/cn';
import { parseDisplayNumber } from '../../../../utils/parseDisplayNumber';
// Styles
import styles from './NumberCell.module.css';
import commonStyles from '../TableCellCommon.module.css';

/**
 * Number cell component for table display with formatting options
 * @param {object} props
 * @param {any} props.value - Numeric value to display
 * @param {string} [props.className] - Additional CSS classes
 * @param {string} [props.locale='fr-FR'] - Locale for number formatting
 * @param {object} [props.formatOptions] - Intl.NumberFormat options
 * @param {string} [props.prefix] - Prefix to add before the number
 * @param {string} [props.suffix] - Suffix to add after the number
 * @param {Align} [props.align] - Text alignment
 * @param {VerticalAlign} [props.verticalAlign='middle'] - Vertical alignment
 * @param {boolean} [props.bold] - Text in bold
 * @param {boolean} [props.italic] - Text in italic
 * @param {boolean} [props.strikeThrough] - Text with strike-through
 * @param {boolean} [props.underline] - Text underlined
 * @returns {JSX.Element}
 */
function NumberCell({
  value,
  className = '',
  locale = 'fr-FR',
  formatOptions = {},
  prefix = '',
  suffix = '',
  align = 'left',
  verticalAlign = 'middle',
  bold = false,
  italic = false,
  strikeThrough = false,
  underline = false,
}) {
  const formattedValue = useMemo(() => {
    if (value === null || value === undefined || value === '') return '';

    const numValue = parseDisplayNumber(value);

    if (Number.isNaN(numValue)) return value;

    try {
      const isPercentStyle = /** @type {any} */ (formatOptions).style === 'percent';
      const valueToFormat = isPercentStyle ? numValue / 100 : numValue;

      return valueToFormat.toLocaleString(locale, formatOptions);
    } catch (error) {
      return numValue.toLocaleString(locale);
    }
  }, [value, locale, formatOptions]);
  const displayValue = useMemo(
    () => `${prefix}${formattedValue}${suffix}`,
    [prefix, formattedValue, suffix],
  );

  return (
    <div
      className={cn([
        styles['number-cell'],
        className,
        bold && commonStyles['cell-bold'],
        italic && commonStyles['cell-italic'],
        strikeThrough && commonStyles['cell-strike-through'],
        underline && commonStyles['cell-underline'],
        align && commonStyles[`cell-align-${align}`],
        verticalAlign && commonStyles[`cell-vertical-align-${verticalAlign}`],
      ])}
    >
      <span
        className={cn([
          styles['number-cell-value'],
          align === 'right' && commonStyles['cell-crop-start'],
        ])}
      >
        {align === 'right' ? <bdi dir="ltr">{displayValue || ''}</bdi> : (displayValue || '')}
      </span>
    </div>
  );
}

export default NumberCell;
